import { Component, computed, effect, inject, input } from '@angular/core';
import { LucideAngularModule, Briefcase, ChevronDown } from 'lucide-angular';

import { TenantService } from '@core/services/tenant.service';
import { MeService } from '@core/services/me.service';

@Component({
  selector: 'app-tenant-selector',
  imports: [LucideAngularModule],
  template: `
    @if (hasManyTenants()) {
      @if (collapsed()) {
        <div class="dropdown dropdown-right">
          <div
            tabindex="0"
            role="button"
            title="Seleccionar tenant"
            class="mx-auto flex size-[52px] items-center justify-center rounded-lg border border-black/20 bg-white text-black shadow-sm dark:border-(--border-secondary) dark:bg-(--bg-elevated) dark:text-(--text-primary)"
          >
            <lucide-angular [img]="Briefcase" class="size-5 shrink-0" [strokeWidth]="2.75" />
          </div>
          <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-1 w-52 p-2 shadow ml-2 max-h-64 flex-nowrap overflow-y-auto">
            @for (t of availableTenants(); track t.tenantKey) {
              <li>
                <a (click)="selectTenant(t.tenantKey)" [class.active]="t.tenantKey === activeTenantKey()">
                  {{ t.displayName }}
                </a>
              </li>
            }
          </ul>
        </div>
      } @else {
        <div class="dropdown dropdown-bottom w-full">
          <div
            tabindex="0"
            role="button"
            class="flex w-full items-center justify-between gap-2 rounded-full border border-black bg-white backdrop-blur-md transition-all duration-200 hover:border-gray-400 hover:bg-gray-50 shadow-sm hover:shadow-md h-12 px-4 py-0 dark:border-(--border-secondary) dark:bg-(--bg-elevated) dark:hover:border-(--accent-primary) dark:hover:bg-(--bg-surface-hover)"
          >
            <div class="flex min-w-0 items-center gap-2">
              <lucide-angular [img]="Briefcase" class="size-4 shrink-0 text-black dark:text-(--text-primary)" />
              <span class="truncate text-sm font-semibold leading-[14px] text-gray-900 dark:text-(--text-primary)">
                {{ selectedTenant()?.displayName ?? 'Cargando...' }}
              </span>
            </div>
            <lucide-angular
              [img]="ChevronDown"
              class="size-4 shrink-0 text-gray-500 dark:text-(--text-secondary)"
            />
          </div>
          <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-1 w-full p-2 shadow mt-1 max-h-64 flex-nowrap overflow-y-auto">
            @for (t of availableTenants(); track t.tenantKey) {
              <li>
                <a (click)="selectTenant(t.tenantKey)" [class.active]="t.tenantKey === activeTenantKey()">
                  {{ t.displayName }}
                </a>
              </li>
            }
          </ul>
        </div>
      }
    }
  `
})
export class TenantSelectorComponent {
  collapsed = input(false);

  private readonly tenantService = inject(TenantService);
  private readonly meService = inject(MeService);

  readonly Briefcase = Briefcase;
  readonly ChevronDown = ChevronDown;

  activeTenantKey = this.tenantService.tenantKey;

  // /api/me is authoritative for every user, including bootstrap admins.
  availableTenants = computed(() => this.meService.me()?.tenants ?? []);

  /** Muestra el selector solo cuando el usuario tiene más de un tenant disponible. */
  readonly hasManyTenants = computed(() => this.availableTenants().length > 1);

  selectedTenant = computed(() => {
    const key = this.activeTenantKey();
    if (!key) return null;
    return this.availableTenants().find(t => t.tenantKey === key) || null;
  });

  constructor() {
    // Auto-seleccionar el único tenant disponible si no hay ninguno activo.
    effect(() => {
      const tenants = this.availableTenants();
      if (tenants.length === 1 && !this.activeTenantKey()) {
        this.tenantService.setTenant(tenants[0].tenantKey);
      }
    });
  }

  selectTenant(tenantKey: string) {
    if (this.activeTenantKey() === tenantKey) {
      return;
    }
    this.tenantService.setTenant(tenantKey);
    window.location.reload();
  }
}
